export interface GetResponseJSON {
  data: { backup_names: string[] };
}

export interface RestoreResponseJSON {
  data: { job_id: string };
}

export interface ListResponseJSON {
  data: { backups: BackupJSON[] };
}

export interface GetTotalResponseJSON {
  data: { total: number };
}

export interface CreateResponseJSON {
  data: BackupSiteReplyJSON;
}

interface BackupJSON {
  database_size: number;
  description: string;
  status: string;
  timestamp: string;
  filesystem_size: number;
  pages: number;
  posts: number;
  wp_version: string;
  fmt_version: string;
  active_subsite_count: number;
  subscribed_subsite_count: number;
  plugins: PluginJSON[];
}

interface PluginJSON {
  name: string;
  status: string;
  version: string;
}

export class Plugin {
  public constructor(
    public name: string,
    public status: string,
    public version: string,
  ) {}

  public static fromJSON(pluginJson: PluginJSON): Plugin {
    return new Plugin(pluginJson.name, pluginJson.status, pluginJson.version);
  }
}

export class Backup {
  private backupMajorVersion: number;

  public constructor(
    private date: string,
    private description: string,
    private status: string,
    private fileSystemSize: number,
    private databaseSize: number,
    private nPosts: number,
    private nPages: number,
    private wpVersion: string,
    private backupVersionString: string,
    private activeSubsiteCount: number,
    private subscribedSubsiteCount: number,
    private plugins: Plugin[],
  ) {
    this.backupMajorVersion = +this.backupVersionString.split('.')[0];
  }

  public get inProgress(): boolean {
    return (
      this.status === 'in-progress' ||
      this.status === 'file-backup' ||
      this.status === 'database-backup' ||
      this.status === 'exporting'
    );
  }

  public get validBackup(): boolean {
    return this.status != 'failed';
  }

  public get displayDescription(): string {
    return this.description;
  }

  public get dateTime(): string {
    return this.date;
  }

  public get hasPlugins(): boolean {
    if (this.backupMajorVersion >= 1) {
      return this.plugins.length > 0;
    }
    return false;
  }

  public get numPlugins(): number {
    return this.plugins.length;
  }

  public get listPlugins(): Plugin[] {
    return this.plugins;
  }

  public get displayFileSystemSize(): number {
    return this.fileSystemSize;
  }

  public get displayDatabaseSize(): number {
    return this.databaseSize;
  }

  public get displayNPosts(): string {
    return this.toDisplayString(this.nPosts);
  }

  public get displayNPages(): string {
    return this.toDisplayString(this.nPages);
  }

  public get displayNPlugins(): string {
    return this.toDisplayString(this.plugins.length);
  }

  public get displayWPVersion(): string {
    return this.toDisplayString(this.wpVersion);
  }

  public get displayActiveSubsiteCount(): string {
    return this.toDisplayString(this.activeSubsiteCount);
  }

  public get displaySubscribedSubsiteCount(): string {
    return this.toDisplayString(this.subscribedSubsiteCount);
  }

  public static fromJSON(backupJSON: BackupJSON): Backup {
    const plugins: Array<Plugin> = [];
    for (const pluginJson of backupJSON.plugins) {
      plugins.push(Plugin.fromJSON(pluginJson));
    }

    return new Backup(
      backupJSON.timestamp,
      backupJSON.description,
      backupJSON.status,
      backupJSON.filesystem_size,
      backupJSON.database_size,
      backupJSON.posts,
      backupJSON.pages,
      backupJSON.wp_version,
      backupJSON.fmt_version,
      backupJSON.active_subsite_count,
      backupJSON.subscribed_subsite_count,
      plugins,
    );
  }

  private toDisplayString(property: any): string {
    if (this.inProgress) {
      return '...';
    }
    if (property === undefined || property === null || this.backupMajorVersion < 1 || this.status === 'failed') {
      return '—';
    }
    return property.toString();
  }
}

interface BackupSiteReplyJSON {
  site_id: string;
}

export class BackupSiteReply {
  public constructor(public siteId: string) {}

  public static fromJSON(json: BackupSiteReplyJSON): BackupSiteReply {
    return new BackupSiteReply(json.site_id);
  }
}

interface StagingFromBackupReplyJSON {
  statusCode: number;
}

export class StagingFromBackupReply {
  public constructor(public statusCode: number) {}

  public static fromJSON(json: StagingFromBackupReplyJSON): StagingFromBackupReply {
    return new StagingFromBackupReply(json.statusCode);
  }
}
